import React from "react";
import styled from "styled-components";
import {
  brand_orange_deep,
  narrowScreenMediaQuery,
} from "../../constants/styles";

const BookIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v17H6.5A2.5 2.5 0 0 0 4 21.5v-17Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M4 21.5A2.5 2.5 0 0 1 6.5 19H20v3H6.5A2.5 2.5 0 0 1 4 19.5v2Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M9 7h7M9 11h7"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const WorkbookIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M5 3h11l4 4v14H5V3Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M16 3v4h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M8 12h8M8 16h8M8 8h4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const ExternalIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M14 4h6v6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20 4 11 13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Inline materials strip — surfaces the lesson's downloadable resources
// (module book + workbook) directly under LessonHero so they're reachable
// near the title/actions row instead of being hidden in the right rail.
// Renders nothing if neither URL is present. Pills wrap to the next line
// at narrow widths via flex-wrap on the strip.
function LessonMaterials({ moduleUrl, workbookUrl }) {
  if (!moduleUrl && !workbookUrl) return null;
  return (
    <Strip aria-label="Materi Pendukung">
      <Label>Materi Pendukung</Label>
      <Pills>
        {moduleUrl && (
          <Pill href={moduleUrl} target="_blank" rel="noopener">
            <PillIcon aria-hidden="true">
              <BookIcon />
            </PillIcon>
            <PillLabel>Buku Materi</PillLabel>
            <PillExternal aria-hidden="true">
              <ExternalIcon width="12" height="12" />
            </PillExternal>
          </Pill>
        )}
        {workbookUrl && (
          <Pill href={workbookUrl} target="_blank" rel="noopener">
            <PillIcon aria-hidden="true">
              <WorkbookIcon />
            </PillIcon>
            <PillLabel>Workbook</PillLabel>
            <PillExternal aria-hidden="true">
              <ExternalIcon width="12" height="12" />
            </PillExternal>
          </Pill>
        )}
      </Pills>
    </Strip>
  );
}

export default React.memo(LessonMaterials);

const Strip = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;

  @media ${narrowScreenMediaQuery} {
    gap: 10px;
  }
`;

const Label = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const Pills = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
`;

const Pill = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 999px;
  background: #fff7ed;
  border: 1px solid rgba(216, 89, 7, 0.32);
  color: ${brand_orange_deep};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-decoration: none;
  white-space: nowrap;
  max-width: 100%;
  transition:
    filter 120ms ease,
    transform 120ms ease,
    background 120ms ease,
    border-color 120ms ease;

  &:hover {
    transform: translateY(-1px);
    background: #ffedd5;
    border-color: rgba(216, 89, 7, 0.5);
    color: ${brand_orange_deep};
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    background: rgba(216, 89, 7, 0.12);
    border-color: rgba(216, 89, 7, 0.32);
    color: #fdba74;

    &:hover {
      background: rgba(216, 89, 7, 0.2);
      border-color: rgba(216, 89, 7, 0.5);
      color: #fdba74;
    }
  }
`;

const PillIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PillLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const PillExternal = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: 0.7;
`;
